import React, { useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { ASSET_TYPES, applyAssetType, assetTypeDefinition, assetTypeIssue } from "../utils/projectAssets";
import { useTranslateText } from "../context/LanguageContext";

// The app translates whole text nodes, so every sentence below is static and
// any number or name it refers to sits in its own node (`detail`).
export default function AssetTypeField({ config, onCommit, windDisabledMessage = "" }) {
  // The type name is displayed in a read-only input, whose text lives in the
  // `value` property. The DOM translator only reaches text nodes, so this one
  // string has to ask for its own Greek.
  const translateText = useTranslateText();
  const [open, setOpen] = useState(false);
  const [nextType, setNextType] = useState("");
  const current = assetTypeDefinition(config.systemType);
  const next = assetTypeDefinition(nextType);
  // Every type stays on the list; the ones the account cannot have are shown
  // disabled, with the caption below saying why. Same treatment, same sentence,
  // as the New asset dialog, so the two places cannot read differently.
  const typeBlocked = (type) => Boolean(type?.hasWind && windDisabledMessage);
  const blockedByLicence = typeBlocked(next);
  const effects = [];
  if (next) {
    if (!next.hasPv && (current?.hasPv || Number(config.pvInstalledCap?.kw) > 0)) {
      effects.push({ text: "PV will be excluded and its installed capacity reset to 0 kWp." });
    }
    if (!next.hasWind && (current?.hasWind || config.wind?.enabled)) {
      effects.push({ text: "Wind generation will be disabled for this asset." });
    }
    if (!next.hasBess && (current?.hasBess || config.bess?.selected?.length)) {
      effects.push({
        text: "BESS will be excluded and its battery selections cleared.",
        detail: `${config.bess?.selected?.length || 0} × BESS`,
      });
    }
    const added = [["hasPv", "PV"], ["hasWind", "Wind"], ["hasBess", "BESS"]]
      .filter(([key]) => next[key] && !current?.[key]).map(([, label]) => label);
    if (added.length) {
      effects.push({ text: "Review the equipment settings after changing the type.", detail: added.join(" + ") });
    }
  }
  return <>
    <Stack spacing={0.5}>
      <TextField fullWidth label="Asset type"
        value={translateText(current?.label || config.systemType || "Not selected")}
        InputProps={{ readOnly: true }}
        InputLabelProps={{ sx: { color: "primary.main", fontWeight: 600 } }} />
      <Box><Button size="small" onClick={() => { setNextType(current?.value || ""); setOpen(true); }}>
        Change asset type
      </Button></Box>
    </Stack>
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm" aria-labelledby="change-asset-type-title">
      <DialogTitle id="change-asset-type-title">Change asset type</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2">
            <span>Current type:</span>{" "}
            <Box component="span" sx={{ fontWeight: 600 }}>{current?.label || config.systemType || "Not selected"}</Box>
          </Typography>
          <TextField select fullWidth label="New asset type" value={nextType} onChange={(e) => setNextType(e.target.value)}>
            {ASSET_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value} disabled={typeBlocked(type)}>
                {type.label}
              </MenuItem>
            ))}
          </TextField>
          {windDisabledMessage ? (
            <Typography variant="caption" color="text.secondary">
              Wind asset types are shown for reference and require Wind System access.
            </Typography>
          ) : null}
          {next && <Typography variant="body2">
            <span>Asset technologies:</span>{" "}
            <Box component="span" sx={{ color: "text.secondary" }}>
              {[[next.hasPv, "PV"], [next.hasWind, "Wind power"], [next.hasBess, "BESS"]]
                .filter(([included]) => included).map(([, label]) => translateText(label)).join(" + ")}
            </Box>
          </Typography>}
          {effects.length > 0 && <Box>
            <Typography variant="subtitle2">Changes to this asset</Typography>
            <Box component="ul" sx={{ pl: 2.5, my: 1 }}>
              {effects.map((effect) => (
                <Typography component="li" variant="body2" key={effect.text} sx={{ mb: 0.5 }}>
                  <span>{effect.text}</span>
                  {effect.detail ? <Box component="span" sx={{ color: "text.secondary" }}>{` ${effect.detail}`}</Box> : null}
                </Typography>
              ))}
            </Box>
          </Box>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>Cancel</Button>
        <Button variant="contained"
          disabled={!next || blockedByLicence || (next.value === current?.value && !assetTypeIssue(config))}
          onClick={() => {
            if (blockedByLicence) return;
            onCommit(applyAssetType(config, nextType));
            setOpen(false);
          }}>
          Apply change
        </Button>
      </DialogActions>
    </Dialog>
  </>;
}
